/**
 * Spec 200 Fase 5 #4 — create-work form.
 *
 * Inputs (mirrors mobile/src/screens/NewWorkScreen.tsx minus the
 * native-only affordances):
 *   - título (required, validation matches WorkDetail's
 *     `validateRequiredTitle`).
 *   - descripción.
 *   - cliente: nombre, teléfono, dirección, NIF/CIF.
 *   - notas internas.
 *
 * Out of scope per spec brief: photos, voice notes, signature.
 *
 * Submit:
 *   1. Generate a 32-hex-char random id (same shape mobile uses).
 *   2. Build a minimal Work with status='pending', `createdAt`
 *      and `updatedAt` set to now.
 *   3. broadcastWork → save in IndexedDB + ship the encrypted
 *      blob to the relay.
 *   4. navigate(`/works/${id}`) so the user lands on the detail
 *      where they can edit anything else.
 *
 * Validation lives in submit instead of per-field so the user can
 * fill the form in any order without seeing errors before they're
 * done. Empty/whitespace title is the only required field.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import { broadcastWork } from '../sync/syncWork';
import type { WebIdentity } from '../store/keys';
import type { Work } from '../types';
import {
  validatePhone,
  validateRequiredTitle,
  validateTaxId,
} from '../utils/validators';
import { PhoneInput } from '../components/PhoneInput';

import './NewWork.css';

interface NewWorkProps {
  identity: WebIdentity;
}

function newWorkId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let hex = '';
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

export function NewWork({ identity }: NewWorkProps) {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientTaxId, setClientTaxId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // E14 — per-field validation; populated on blur and on submit so
  // the user sees inline feedback without errors before they're done.
  const [fieldErrors, setFieldErrors] = useState<{
    title?: string | null;
    phone?: string | null;
    taxId?: string | null;
  }>({});

  // Treat any non-empty field as a "dirty" form. Used by the
  // Cancel button + browser-back guard to avoid losing typed
  // content on a misclick. NN/g "Confirmation Dialogs and Error
  // Prevention" + WCAG 3.3.4.
  const dirty = !submitting && Boolean(
    title || description || clientName || clientPhone || clientAddress || clientTaxId || notes,
  );

  function leaveWithGuard(): void {
    if (dirty && !window.confirm('Tienes cambios sin guardar. ¿Salir igualmente?')) return;
    navigate(-1);
  }

  // Block native unloads (refresh, tab close, browser back via OS
  // gesture) only while there's something to lose. The guard is
  // removed automatically on submit (`submitting` flips dirty
  // false during the save flight).
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      // Modern browsers ignore the custom message but still show
      // the native "leave site?" prompt as long as we set
      // returnValue. Pin both to be safe across vendors.
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Run every validator on submit so the user can't bypass an
    // unblurred field. The first failure aborts and surfaces the
    // matching field-level message.
    const titleError = validateRequiredTitle(title);
    const phoneError = validatePhone(clientPhone);
    const taxIdError = validateTaxId(clientTaxId);
    setFieldErrors({ title: titleError, phone: phoneError, taxId: taxIdError });
    if (titleError) {
      setError(titleError);
      return;
    }
    if (phoneError) {
      setError(phoneError);
      return;
    }
    if (taxIdError) {
      setError(taxIdError);
      return;
    }
    setError(null);
    setSubmitting(true);

    const id = newWorkId();
    const now = Date.now();
    // Spec 200 Fase 5 also wires statusHistory on creation so the
    // detail timeline starts with one entry — mobile does the same.
    const work: Work = {
      id,
      title: title.trim(),
      status: 'pending',
      createdAt: now,
      updatedAt: now,
      description: description.trim() || undefined,
      clientName: clientName.trim() || undefined,
      clientPhone: clientPhone.trim() || undefined,
      clientAddress: clientAddress.trim() || undefined,
      clientTaxId: clientTaxId.trim() || undefined,
      notes: notes.trim() || undefined,
      technicianId: identity.technicianId,
      statusHistory: [
        { status: 'pending', name: identity.name, at: now },
      ],
    };
    try {
      await broadcastWork(work, identity);
      navigate(`/works/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
      setSubmitting(false);
    }
  }

  return (
    <div className="nw-shell" data-testid="newwork-screen">
      <header className="nw-header">
        <button
          type="button"
          className="nw-back"
          onClick={leaveWithGuard}
          data-testid="newwork-back"
        >
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </button>
      </header>

      <form
        id="main-content"
        className="nw-form"
        onSubmit={submit}
        data-testid="newwork-form"
        tabIndex={-1}
      >
        <h1 className="nw-title">Nuevo trabajo</h1>
        <p className="nw-tagline">
          Solo el título es obligatorio. El resto puedes completarlo después desde el detalle.
        </p>

        <label className="nw-field">
          <span className="nw-label">
            Título <span className="nw-required">*</span>
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() =>
              setFieldErrors((prev) => ({ ...prev, title: validateRequiredTitle(title) }))
            }
            data-testid="newwork-title"
            placeholder="Reparación caldera, sustitución termo…"
            aria-invalid={Boolean(fieldErrors.title)}
            aria-describedby={fieldErrors.title ? 'nw-error-title' : undefined}
            autoFocus
            required
          />
          {fieldErrors.title && (
            <span id="nw-error-title" className="nw-field-error" data-testid="newwork-title-error">
              {fieldErrors.title}
            </span>
          )}
        </label>

        <label className="nw-field">
          <span className="nw-label">Descripción</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="newwork-description"
            placeholder="Detalles del trabajo, instrucciones especiales…"
            rows={4}
          />
        </label>

        <fieldset className="nw-fieldset">
          <legend>Cliente</legend>
          <label className="nw-field">
            <span className="nw-label">Nombre</span>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              data-testid="newwork-client-name"
              placeholder="Nombre del cliente"
              autoComplete="name"
            />
          </label>
          <div className="nw-field">
            <span className="nw-label">Teléfono</span>
            {/* Spec 219 — flag picker + dial code + per-country
                grouping + E.164 normalisation. */}
            <PhoneInput
              value={clientPhone}
              onChange={setClientPhone}
              onBlur={() =>
                setFieldErrors((prev) => ({ ...prev, phone: validatePhone(clientPhone) }))
              }
              testId="newwork-client-phone"
              status={fieldErrors.phone ? 'error' : 'idle'}
              ariaInvalid={Boolean(fieldErrors.phone)}
              ariaDescribedBy={fieldErrors.phone ? 'nw-error-phone' : undefined}
              ariaLabel="Teléfono del cliente"
              placeholder="612 345 678"
            />
            {fieldErrors.phone && (
              <span id="nw-error-phone" className="nw-field-error" data-testid="newwork-phone-error">
                {fieldErrors.phone}
              </span>
            )}
          </div>
          <label className="nw-field">
            <span className="nw-label">Dirección</span>
            <input
              type="text"
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
              data-testid="newwork-client-address"
              placeholder="Calle, número, piso…"
              autoComplete="street-address"
            />
          </label>
          <label className="nw-field">
            <span className="nw-label">NIF/CIF</span>
            <input
              type="text"
              value={clientTaxId}
              onChange={(e) => setClientTaxId(e.target.value)}
              onBlur={() =>
                setFieldErrors((prev) => ({ ...prev, taxId: validateTaxId(clientTaxId) }))
              }
              data-testid="newwork-client-taxid"
              placeholder="X1234567Z"
              aria-invalid={Boolean(fieldErrors.taxId)}
              aria-describedby={fieldErrors.taxId ? 'nw-error-taxid' : undefined}
            />
            {fieldErrors.taxId && (
              <span id="nw-error-taxid" className="nw-field-error" data-testid="newwork-taxid-error">
                {fieldErrors.taxId}
              </span>
            )}
          </label>
        </fieldset>

        <label className="nw-field">
          <span className="nw-label">Notas internas</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="newwork-notes"
            placeholder="Recordatorios, instrucciones internas…"
            rows={3}
          />
        </label>

        {error && (
          <p className="nw-error" data-testid="newwork-error" role="alert">
            {error}
          </p>
        )}

        <div className="nw-actions">
          <button
            type="button"
            className="nw-btn nw-btn--secondary"
            onClick={leaveWithGuard}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="nw-btn nw-btn--primary"
            data-testid="newwork-submit"
            disabled={submitting}
          >
            {submitting ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
}
